import * as Actions from '@actions/index';
import { AppState, selectDataFromCommonEntity, selectEntity } from '@reducers/index';
import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { CustomerLogo, DashletVoiceQualityActiveStreams } from '@models/index';
import { DashletLineGraphComponent } from '../dashlet-line-graph/dashlet-line-graph.component';
import {
    DashletSettingsService,
    DashletService,
    TimeService,
    DashletDataProcessService,
    TimeoutService,
    RealtimeGatewayService
} from '@services/index';
import { select, Store } from '@ngrx/store';
import { Subscription } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';

@Component({
    selector: 'app-dashlet-voice-quality-active-streams',
    templateUrl: './dashlet-voice-quality-active-streams.component.html',
    styleUrls: ['./dashlet-voice-quality-active-streams.component.scss']
})
export class DashletVoiceQualityActiveStreamsComponent implements OnInit, OnDestroy {
    @Input() dashlet: DashletVoiceQualityActiveStreams;
    private hasPreviousSubscriptions: boolean = false;
    private hasConnected = false;
    private disconnectedAt: Date | null = null;
    private pendingBackfill: Map<string, Date> = new Map();

    interval: number = 20;

    private pChart: DashletLineGraphComponent;
    get chart() {
        return this.pChart;
    }

    @ViewChild('chart') set chart(value: DashletLineGraphComponent) {
        this.pChart = value;
        this.updateChart();
    }

    private pLargeChart: DashletLineGraphComponent;
    get largeChart() {
        return this.pLargeChart;
    }
    @ViewChild('largeChart') set largeChart(value: DashletLineGraphComponent) {
        this.pLargeChart = value;
        this.updateChart();
    }

    private highQoS = {
        label: 'Good',
        pointRadius: 0,
        pointHitRadius: 10,
        borderColor: 'white',
        backgroundColor: this.dashletService.getSeverityColor(0),
        pointBorderColor: this.dashletService.getSeverityColor(0),
        pointBackgroundColor: this.dashletService.getSeverityColor(0),
        pointHighlightStroke: 'white',
        fill: true,
        spanGap: false,
        data: []
    };

    private averageQoS = {
        label: 'Satisfactory',
        pointRadius: 0,
        pointHitRadius: 10,
        borderColor: 'white',
        backgroundColor: this.dashletService.getSeverityColor(1),
        pointBorderColor: this.dashletService.getSeverityColor(1),
        pointBackgroundColor: this.dashletService.getSeverityColor(1),
        pointHighlightStroke: 'white',
        fill: true,
        spanGap: false,
        data: []
    };

    private poorQoS = {
        label: 'Poor',
        pointRadius: 0,
        pointHitRadius: 10,
        borderColor: 'white',
        backgroundColor: this.dashletService.getSeverityColor(2),
        pointBorderColor: this.dashletService.getSeverityColor(2),
        pointBackgroundColor: this.dashletService.getSeverityColor(2),
        pointHighlightStroke: 'white',
        fill: true,
        spanGap: false,
        data: []
    };

    private unkownQoS = {
        label: 'Unknown',
        pointRadius: 0,
        pointHitRadius: 10,
        borderColor: 'white',
        backgroundColor: this.dashletService.getSeverityColor(-1),
        pointBorderColor: this.dashletService.getSeverityColor(-1),
        pointBackgroundColor: this.dashletService.getSeverityColor(-1),
        pointHighlightStroke: 'white',
        fill: true,
        spanGap: false,
        data: []
    };

    lineData: any = {};
    chartOptions: any = {
        scales: {
            x: {
                type: 'time',
                grid: {
                    display: false
                },
                time: {
                    unit: 'minute',
                    displayFormats: {
                        minute: 'HH:mm'
                    },
                    stepSize: 6,
                    max: null,
                    min: null
                },
                ticks: {
                    fontSize: 10.2,
                    autoSkip: false,
                    maxRotation: 0,
                    minRotation: 0
                }
            },
            y: {
                type: 'linear',
                min: 0,
                stacked: true,
                grid: {
                    display: false
                },
                beginAtZero: true,
                ticks: {
                    fontSize: 10.2,
                    suggestedMin: 1,
                    callback: function (value) {
                        if (value % 1 === 0) {
                            return value;
                        }
                    }
                }
            }
        },
        plugins: {
            legend: {
                display: false,
                position: 'bottom',
                reverse: true
            },
            tooltip: {
                callback: {
                    title: function () {},
                    footer: tipItem => {
                        let string = [];
                        string.push(tipItem[0].xLabel.toString().split(' ').slice(4).join(' ').split('(')[0]);
                        string.push('(' + tipItem[0].xLabel.toString().split(' ').slice(4).join(' ').split('(')[1]);
                        return string;
                    }
                }
            },
            filler: {
                propagate: true
            }
        },

        animation: {
            duration: 0
        },
        interaction: {
            mode: 'index',
            intersect: false
        }
    };

    largeChartOptions: any = {
        scales: {
            x: {
                type: 'time',
                grid: {
                    display: false
                },
                time: {
                    unit: 'minute',
                    displayFormats: {
                        minute: 'HH:mm'
                    },
                    stepSize: 6,
                    max: null,
                    min: null
                },
                ticks: {
                    fontSize: 10.2,
                    autoSkip: false,
                    maxRotation: 0,
                    minRotation: 0
                }
            },
            y: {
                type: 'linear',
                min: 0,
                stacked: true,
                grid: {
                    display: false
                },
                beginAtZero: true,
                ticks: {
                    fontSize: 10.2,
                    suggestedMin: 1,
                    callback: function (value) {
                        if (value % 1 === 0) {
                            return value;
                        }
                    }
                }
            }
        },
        plugins: {
            legend: {
                display: false,
                position: 'bottom',
                reverse: true
            },
            tooltip: {
                callback: {
                    title: function () {},
                    footer: tipItem => {
                        let string = [];
                        string.push(tipItem[0].xLabel.toString().split(' ').slice(4).join(' ').split('(')[0]);
                        string.push('(' + tipItem[0].xLabel.toString().split(' ').slice(4).join(' ').split('(')[1]);
                        return string;
                    }
                }
            },
            filler: {
                propagate: true
            }
        },

        animation: {
            duration: 0
        },
        interaction: {
            mode: 'index',
            intersect: false
        }
    };

    private subscription: Subscription = new Subscription();
    private subscriptionDocument: Subscription = new Subscription();

    constructor(
        public dashletService: DashletService,
        private settingsService: DashletSettingsService,
        private store$: Store<AppState>,
        private dashletDataService: DashletDataProcessService,
        private timeService: TimeService,
        public timeoutService: TimeoutService,
        private realtimeService: RealtimeGatewayService
    ) {
        this.lineData = {
            datasets: [this.unkownQoS, this.poorQoS, this.averageQoS, this.highQoS]
        };
    }

    ngOnInit() {
        this.store$.dispatch(Actions.GetEntityLogo({ entityId: this.dashlet.customer.customerId }));
        this.subscription.add(
            this.store$.pipe(select(selectEntity(this.dashlet.customer.customerId))).subscribe(logo => {
                if (logo) {
                    this.dashlet.logo = new CustomerLogo(logo.image, logo.imageType);
                }
            })
        );

        this.subscription.add(
            this.dashlet.onDataUpdated.subscribe(() => {
                this.updateChart();
            })
        );
        this.updateChart();
        this.subscription.add(
            this.dashlet.equipmentLoaded.subscribe(() => {
                if (this.hasPreviousSubscriptions) {
                    this.subscriptionDocument.unsubscribe();
                    this.subscriptionDocument = new Subscription();
                }
                this.subscribeToDocument();
            })
        );
        this.subscribeToDocument();

        this.subscription.add(
            this.realtimeService.isConnectedSubject
                .pipe(distinctUntilChanged())
                .subscribe(connected => this.onConnectionChanged(connected))
        );
    }

    private onConnectionChanged(connected: boolean): void {
        if (!connected) {
            if (this.hasConnected && !this.disconnectedAt) {
                this.disconnectedAt = new Date();
            }
            return;
        }

        const disconnectedAt = this.disconnectedAt;
        this.disconnectedAt = null;
        if (this.hasConnected && disconnectedAt) {
            this.backfill(disconnectedAt);
        }
        this.hasConnected = true;
    }

    private backfill(disconnectedAt: Date): void {
        if (this.dashlet.equipmentIds && this.dashlet.locationId && this.dashlet.customer.customerId) {
            // Start one interval before disconnect so we get anything partially received
            const from = new Date(
                Math.max(disconnectedAt.getTime() - this.dashlet.interval * 1000, Date.now() - 60 * 60 * 1000)
            );
            this.dashlet.startBackfill();
            this.pendingBackfill.clear();

            this.dashlet.equipmentIds.forEach(equipmentId => {
                this.pendingBackfill.set(equipmentId, from);
                this.store$.dispatch(
                    Actions.SubscribeToRealTimeService({
                        equipmentId: equipmentId,
                        command: this.dashlet.commandTypeIdRtcpNew
                    })
                );
                this.store$.dispatch(
                    Actions.GetCommonHistoric({
                        equipmentId: equipmentId,
                        commandTypeId: this.dashlet.commandTypeIdRtcpNew,
                        from: from.toISOString(),
                        to: new Date().toISOString()
                    })
                );
            });
        }
    }

    private subscribeToDocument(): void {
        if (this.dashlet.equipmentIds && this.dashlet.locationId && this.dashlet.customer.customerId) {
            this.dashlet.resetData();
            this.subscriptionDocument.add(
                this.settingsService.getEquipment(this.dashlet.locationId, 'ACM').subscribe(receivers => {
                    receivers.forEach(acmId => {
                        this.store$.dispatch(
                            Actions.SubscribeToRealTimeService({
                                equipmentId: acmId['value'],
                                command: this.dashlet.commandTypeIdIpnrLookup
                            })
                        );
                        this.store$.dispatch(
                            Actions.GetNotifyCommonEntitys({
                                equipmentId: acmId['value'],
                                commandTypeId: this.dashlet.commandTypeIdIpnrLookup
                            })
                        );

                        this.subscriptionDocument.add(
                            this.store$
                                .pipe(
                                    select(
                                        selectDataFromCommonEntity(
                                            acmId['value'] + this.dashlet.commandTypeIdIpnrLookup
                                        )
                                    )
                                )
                                .subscribe(data => {
                                    if (data) this.dashlet.ipnrMap.updateMap(data);
                                })
                        );
                    });
                })
            );

            this.dashlet.equipmentIds.forEach(equipmentId => {
                const key = equipmentId + ':' + this.dashlet.commandTypeIdRtcpNew;
                this.store$.dispatch(
                    Actions.SubscribeToRealTimeService({
                        equipmentId: equipmentId,
                        command: this.dashlet.commandTypeIdRtcpNew
                    })
                );
                this.store$.dispatch(
                    Actions.GetNotifyCommonEntitys({
                        equipmentId: equipmentId,
                        commandTypeId: this.dashlet.commandTypeIdRtcpNew
                    })
                );
                this.subscriptionDocument.add(
                    this.store$
                        .pipe(select(selectDataFromCommonEntity(equipmentId + this.dashlet.commandTypeIdRtcpNew)))
                        .subscribe(data => {
                            const backfillFrom = this.pendingBackfill.get(equipmentId);
                            // Historic results come in multiple documents unlike the normal real time ones
                            if (backfillFrom && Array.isArray(data) && data.length > 1) {
                                this.pendingBackfill.delete(equipmentId);
                                this.dashlet.processRtcpBackfill(key, data, backfillFrom);
                                return;
                            }

                            if (
                                !data?.[0] ||
                                this.timeoutService.getIsDataExpired(
                                    this.dashlet.commandTypeIdRtcpNew,
                                    data[0].timestamp
                                )
                            )
                                return;

                            this.dashlet.processRtcpNew(key, data);
                        })
                );
            });
            this.hasPreviousSubscriptions = true;
        }
    }

    public ngOnDestroy(): void {
        this.subscriptionDocument.unsubscribe();
        this.subscription.unsubscribe();
    }

    private updateChart() {
        this.highQoS.data = this.dashletDataService.dedupe(this.dashlet.mosDataGood).map(point => {
            return { ...point, x: new Date(this.timeService.convertToUserTimeZone(point.x)) };
        });
        this.averageQoS.data = this.dashletDataService.dedupe(this.dashlet.mosDataSatisfactory).map(point => {
            return { ...point, x: new Date(this.timeService.convertToUserTimeZone(point.x)) };
        });
        this.poorQoS.data = this.dashletDataService.dedupe(this.dashlet.mosDataPoor).map(point => {
            return { ...point, x: new Date(this.timeService.convertToUserTimeZone(point.x)) };
        });
        this.unkownQoS.data = this.dashletDataService.dedupe(this.dashlet.mosDataUnknown).map(point => {
            return { ...point, x: new Date(this.timeService.convertToUserTimeZone(point.x)) };
        });

        this.lineData = {
            datasets: [this.unkownQoS, this.poorQoS, this.averageQoS, this.highQoS]
        };

        const dateString = new Date();
        const max = new Date(this.timeService.convertToUserTimeZone(dateString));
        max.setSeconds(Math.floor(max.getSeconds() / this.interval) * this.interval, 0);
        const min = new Date(max);

        if (this.dashlet) {
            if (this.dashlet.getSize().id === 0) {
                min.setMinutes(min.getMinutes() - 30);
            } else if (this.dashlet.getSize().id === 1) {
                min.setMinutes(min.getMinutes() - 60);
            }
        }
        this.chartOptions.scales.x.min = min;
        this.chartOptions.scales.x.max = max;

        if (this.chart) this.chart.updateChart();
        if (this.largeChart) this.largeChart.updateChart();
    }
}
